import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import RecyclingIcon from '@mui/icons-material/Recycling';
import DeleteIcon from '@mui/icons-material/Delete';
import CompostIcon from '@mui/icons-material/Compost';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const WasteManagement = () => {
  const wasteStreams = [
    { type: 'Food Waste', amount: '45 kg', percentage: 35, change: -12, color: '#8b5cf6', icon: <CompostIcon /> },
    { type: 'Recycling', amount: '32 kg', percentage: 25, change: 8, color: '#3b82f6', icon: <RecyclingIcon /> },
    { type: 'General Waste', amount: '28 kg', percentage: 22, change: -5, color: '#6b7280', icon: <DeleteIcon /> },
    { type: 'Packaging', amount: '15 kg', percentage: 12, change: -3, color: '#f59e0b', icon: <LocalShippingIcon /> },
    { type: 'Glass', amount: '10 kg', percentage: 8, change: 15, color: '#0ea5e9', icon: <DeleteIcon /> },
  ];

  const diversionRates = [
    { material: 'Paper & Cardboard', rate: 85, target: 90 },
    { material: 'Plastic', rate: 65, target: 80 },
    { material: 'Glass', rate: 72, target: 85 },
    { material: 'Metal', rate: 88, target: 95 },
    { material: 'Organic', rate: 42, target: 70 },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Waste Management
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track waste generation, recycling rates, and implement reduction strategies.
      </Typography>
      
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#10b98120', color: '#10b981' }}>
                  <RecyclingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Total Waste
                  </Typography>
                  <Typography variant="h3" color="primary">
                    130 kg
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon sx={{ color: '#10b981' }} />
                <Typography variant="body2" color="#10b981" fontWeight="bold">
                  -8.5% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#3b82f620', color: '#3b82f6' }}>
                  <TrendingDownIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Diversion Rate
                  </Typography>
                  <Typography variant="h3" color="#3b82f6">
                    65%
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: '#10b981' }} />
                <Typography variant="body2" color="#10b981" fontWeight="bold">
                  +5.2% improvement
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#8b5cf620', color: '#8b5cf6' }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Cost Savings
                  </Typography>
                  <Typography variant="h3" color="#8b5cf6">
                    $320
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                From waste reduction initiatives
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Waste Streams Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Waste Streams Breakdown
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell><strong>Waste Type</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell align="right"><strong>% of Total</strong></TableCell>
                      <TableCell align="right"><strong>Trend</strong></TableCell>
                      <TableCell align="right"><strong>Target</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wasteStreams.map((stream, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ color: stream.color }}>
                              {stream.icon}
                            </Box>
                            {stream.type}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {stream.amount}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>{stream.percentage}%</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {stream.change >= 0 ? (
                              <TrendingUpIcon sx={{ color: '#ef4444', fontSize: 16 }} />
                            ) : (
                              <TrendingDownIcon sx={{ color: '#10b981', fontSize: 16 }} />
                            )}
                            <Typography color={stream.change >= 0 ? '#ef4444' : '#10b981'}>
                              {stream.change >= 0 ? '+' : ''}{stream.change}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={stream.type === 'Food Waste' ? 'Reduce 50%' : 
                                   stream.type === 'Recycling' ? 'Increase 20%' : 
                                   stream.type === 'General Waste' ? 'Reduce 30%' : 'Maintain'}
                            size="small"
                            color={stream.type === 'Food Waste' ? 'error' : 
                                   stream.type === 'Recycling' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Diversion Rate by Material
              </Typography>
              {diversionRates.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{item.material}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.rate}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={item.rate} 
                      sx={{ 
                        flexGrow: 1,
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: '#e5e7eb',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: item.rate >= item.target - 10 ? '#10b981' : 
                                         item.rate >= item.target - 20 ? '#f59e0b' : '#ef4444',
                          borderRadius: 4,
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {item.target}%
                    </Typography>
                  </Box>
                </Box>
              ))}
              
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#ecfdf5', borderRadius: 2 }}>
                <Typography variant="body2" color="#065f46" fontWeight="medium">
                  🎯 Zero Waste Goal
                </Typography>
                <Typography variant="caption" color="#065f46">
                  Target: 90% diversion rate by 2024
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={65} 
                  sx={{ 
                    mt: 1,
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: '#d1fae5',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#10b981',
                      borderRadius: 3,
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Waste Reduction Initiatives */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Waste Reduction Initiatives
          </Typography>
          <Grid container spacing={2}>
            {[
              { 
                title: 'Food Donation Program', 
                status: 'Active',
                impact: 'Reduced food waste by 25kg/month',
                icon: '🥫'
              },
              { 
                title: 'Composting System', 
                status: 'In Progress',
                impact: 'Diverting 15kg organic waste weekly',
                icon: '♻️'
              },
              { 
                title: 'Reusable Packaging', 
                status: 'Planned',
                impact: 'Reduce packaging waste by 40%',
                icon: '📦'
              },
              { 
                title: 'Supplier Recycling', 
                status: 'Active',
                impact: 'Closed-loop system with 3 suppliers',
                icon: '🔄'
              },
            ].map((initiative, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'grey.100', fontSize: 24 }}>
                        {initiative.icon}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6">{initiative.title}</Typography>
                        <Chip 
                          label={initiative.status}
                          size="small"
                          color={initiative.status === 'Active' ? 'success' : 
                                 initiative.status === 'In Progress' ? 'warning' : 'default'}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {initiative.impact}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Last updated: 2 weeks ago
                      </Typography>
                      <Chip 
                        label="View Details" 
                        size="small" 
                        variant="outlined"
                        clickable
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WasteManagement;